import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientTaxInfoSchema, ClientTaxInfo } from '@/lib/clientValidation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, X } from 'lucide-react';
import { useState } from 'react';

interface ClientTaxInfoTabProps {
  defaultValues: ClientTaxInfo;
  onSave: (data: ClientTaxInfo) => Promise<void>;
  isSaving: boolean;
}

export default function ClientTaxInfoTab({
  defaultValues,
  onSave,
  isSaving,
}: ClientTaxInfoTabProps) {
  const [dependents, setDependents] = useState(defaultValues.dependents || []);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ClientTaxInfo>({
    resolver: zodResolver(clientTaxInfoSchema),
    defaultValues: {
      ...defaultValues,
      dependents,
    },
  });

  const handleAddDependent = () => {
    setDependents([...dependents, { name: '', age: undefined, relationship: '' }]);
  };

  const handleRemoveDependent = (index: number) => {
    setDependents(dependents.filter((_, i) => i !== index));
  };

  const handleDependentChange = (index: number, field: string, value: any) => {
    const updated = [...dependents];
    updated[index] = { ...updated[index], [field]: value };
    setDependents(updated);
  };

  const onSubmit = (data: ClientTaxInfo) => {
    return onSave({ ...data, dependents });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="tax_year">Tax Year *</Label>
          <Input
            id="tax_year"
            type="number"
            {...register('tax_year', { valueAsNumber: true })}
            placeholder="2025"
          />
          {errors.tax_year && (
            <p className="text-sm text-destructive mt-1">{errors.tax_year.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="filing_status">Filing Status</Label>
          <Input
            id="filing_status"
            {...register('filing_status')}
            placeholder="Single, Married Filing Jointly, etc."
          />
          {errors.filing_status && (
            <p className="text-sm text-destructive mt-1">{errors.filing_status.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="ssn_last_4">SSN Last 4 Digits</Label>
          <Input
            id="ssn_last_4"
            {...register('ssn_last_4')}
            placeholder="****"
            maxLength={4}
          />
          {errors.ssn_last_4 && (
            <p className="text-sm text-destructive mt-1">{errors.ssn_last_4.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="previous_year_agi">Previous Year AGI</Label>
          <Input
            id="previous_year_agi"
            type="number"
            {...register('previous_year_agi', { valueAsNumber: true })}
            placeholder="50000"
          />
          {errors.previous_year_agi && (
            <p className="text-sm text-destructive mt-1">{errors.previous_year_agi.message}</p>
          )}
        </div>

        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Dependents</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddDependent}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Dependent
            </Button>
          </div>

          {dependents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No dependents added</p>
          ) : (
            <div className="space-y-3">
              {dependents.map((dependent, index) => (
                <div key={index} className="flex gap-2 items-start p-3 border rounded-md">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Name"
                      value={dependent.name}
                      onChange={(e) => handleDependentChange(index, 'name', e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="number"
                        placeholder="Age"
                        value={dependent.age || ''}
                        onChange={(e) => handleDependentChange(index, 'age', parseInt(e.target.value) || undefined)}
                      />
                      <Input
                        placeholder="Relationship"
                        value={dependent.relationship || ''}
                        onChange={(e) => handleDependentChange(index, 'relationship', e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveDependent(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          type="submit"
          disabled={isSaving || !isDirty}
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </form>
  );
}
